import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// ─── Auth ───────────────────────────────────────────────────────────────────
import { UnifiedAuth } from '../pages/auth';

// ─── Shared (Dashboard — accessible to all roles) ───────────────────────────
import Dashboard from '../features/dashboard/pages/Dashboard';
import PresentationConfig from '../features/Presentation_config/pages/PresentationConfig';

// ─── Admin Role ──────────────────────────────────────────────────────────────
import { UsersRoles } from '../pages/admin';

// ─── Bid Manager (Team Lead) Role ────────────────────────────────────────────
import {
    Dashboard as BidManagerDashboard,
    RfpDetails,
    PPTCustomize,
    PPTThemePage,
    Projects,
    Analytics,
    ContentLibrary,
    Team,
    Settings,
    AuditLogs
} from '../pages/bid-manager';

// ─── Editor (Team Member) Role ───────────────────────────────────────────────
import {
    Dashboard as EditorDashboard,
    RFPIntake,
    Requirements
} from '../pages/editor';


/**
 * Returns true if the logged-in user is a Team Lead / Manager / Admin.
 * Uses the same comprehensive check as RFPAnalysisResult.jsx (designation + role).
 */
const isTeamLead = (user) => {
    const designation = (user?.designation || '').toLowerCase();
    const role = (user?.role || '').toLowerCase();
    return (
        designation.includes('lead') || designation.includes('manager') || designation.includes('admin') ||
        role.includes('lead') || role.includes('manager') || role.includes('admin')
    );
};

/**
 * Authenticated routes — rendered when the user is logged in.
 */
export const AuthenticatedRoutes = ({ user, globalSearch }) => (
    <Routes>
        <Route
            path="/"
            element={<Navigate to="/dashboard" replace />}
        />
        <Route
            path="/dashboard"
            element={<Dashboard user={user} />}
        />
        <Route path="/proposals" element={<BidManagerDashboard user={user} globalSearch={globalSearch} />} />
        {/* /my-proposals is for Team Members only — Team Leads are redirected to /proposals */}
        <Route
            path="/my-proposals"
            element={
                isTeamLead(user)
                    ? <Navigate to="/proposals" replace />
                    : <EditorDashboard user={user} globalSearch={globalSearch} />
            }
        />
        <Route path="/proposals/new" element={<RFPIntake />} />

        <Route path="/proposals/analysis-result/:rfpId" element={<RfpDetails />} />
        <Route path="/proposals/analysis-result/:rfpId/secid/:secId" element={<RfpDetails />} />
        <Route path="/proposals/ppt-customization/:rfpId" element={<PPTCustomize />} />
        <Route path="/proposals/ppt-customization/:rfpId/theme" element={
            isTeamLead(user) ? <PPTThemePage /> : <Navigate to="/dashboard" replace />
        } />
        <Route path="/themes" element={
            isTeamLead(user) ? <PPTThemePage /> : <Navigate to="/dashboard" replace />
        } />
        <Route path="/projects" element={<Projects user={user} />} />
        <Route path="/requirements" element={<Requirements />} />
        <Route path="/library" element={<ContentLibrary />} />
        <Route path="/team" element={<Team />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/audit-logs" element={<AuditLogs />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/presentation" element={<PresentationConfig />} />
        <Route path="/users-roles" element={<UsersRoles />} />
        <Route path="*" element={<BidManagerDashboard user={user} />} />
    </Routes>
);

/**
 * Public (unauthenticated) routes.
 */
export const PublicRoutes = ({ onLogin }) => (
    <Routes>
        <Route path="/login" element={<UnifiedAuth onLogin={onLogin} />} />
        <Route path="/signup" element={<UnifiedAuth onLogin={onLogin} />} />
        <Route path="/forgot-password" element={<UnifiedAuth onLogin={onLogin} />} />
        <Route path="*" element={<UnifiedAuth onLogin={onLogin} />} />
    </Routes>
);
